'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import UiLogo from '@/components/ui/UiLogo';
import { UiButton } from '@/components/ui/UiButton';
import { UiIcon } from '@/components/ui/UiIcon';
import { NavLink, getAnchorIds, getHeaderCtas, isNavItemActive } from '@/features/navigation';
import { useSiteSettings } from '@/components/providers';
import { useScrolled, useScrollSpy } from '@/hooks';
import { telHref } from '@/lib/utils/contact';
import { cn } from '@/lib/utils/tw-merge';

// Past this offset the bar tightens and shows its line and shadow (design: 12px).
const SCROLLED_OFFSET = 12;

// Only viewports below the nav breakpoint ever open this panel, so it loads on
// demand. `ssr: false` inside this `'use client'` module is what actually moves
// the bytes (see AGENTS.md → Performance).
const NavigationOffCanvas = dynamic(
    () => import('@/features/navigation/NavigationOffCanvas').then((m) => m.NavigationOffCanvas),
    { ssr: false },
);

interface HeaderProps {
    className?: string;
    /** Whether the header overlays content (fixed) or takes space (sticky). */
    position?: 'sticky' | 'fixed';
}

function Header({ className, position = 'sticky' }: HeaderProps) {
    const settings = useSiteSettings();
    const pathname = usePathname();

    const items = settings.navigation?.main ?? [];
    const activeAnchorId = useScrollSpy({ ids: getAnchorIds(items) });
    const isScrolled = useScrolled(SCROLLED_OFFSET);

    const phone = settings.contactData?.contactPhone;
    const ctas = getHeaderCtas(settings.header);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // Mounting the lazy panel is what triggers its request, so it is gated
    // separately: pointer/focus on the trigger loads the chunk, the click then
    // only flips the panel open.
    const [isMenuMounted, setIsMenuMounted] = useState(false);

    return (
        <>
            <header
                className={cn(
                    position === 'fixed' ? 'fixed inset-x-0 top-0 z-50' : 'sticky inset-x-0 top-0 z-50',
                    'site-header',
                    className,
                )}
                data-scrolled={isScrolled}
            >
                <div className="site-header-container flex items-center justify-between gap-[22px]">
                    <div className="flex items-center gap-(--sp-lg) min-w-0">
                        <Link
                            href="/"
                            aria-label={settings.siteTitle}
                            className="site-logo-link site-header__logo inline-flex max-w-full"
                        >
                            <UiLogo variant="wordmark" className="site-logo" />
                        </Link>

                        <nav className="site-nav" aria-label="Hauptnavigation">
                            {items.map((item) => (
                                <NavLink
                                    key={item._key}
                                    item={item}
                                    isActive={isNavItemActive(item, pathname, activeAnchorId)}
                                />
                            ))}
                        </nav>
                    </div>

                    <div className="site-header__right">
                        {phone ? (
                            <a
                                href={telHref(phone)}
                                className="site-header__phone min-h-11 items-center gap-2 font-(family-name:--font-additional) text-sm font-medium whitespace-nowrap"
                            >
                                <UiIcon name="phone" size={17} className="site-header__phone-icon" aria-hidden="true" />
                                {phone}
                            </a>
                        ) : null}

                        {/* One button per page tone. Which one stands, and whether it
                            waits for the page's lead action, is CSS (header.css),
                            so nothing here waits for hydration. */}
                        {ctas.map(({ tone, item }) => (
                            <div key={tone} className="site-header__cta-wrap" data-tone={tone}>
                                <div className="site-header__cta-inner">
                                    <UiButton item={item} size="sm" className="site-header__cta" />
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={() => {
                                setIsMenuMounted(true);
                                setIsMenuOpen(true);
                            }}
                            onPointerEnter={() => setIsMenuMounted(true)}
                            onFocus={() => setIsMenuMounted(true)}
                            aria-label="Menü öffnen"
                            aria-haspopup="dialog"
                            aria-expanded={isMenuOpen}
                            className="site-burger shape-pill size-11 shrink-0 cursor-pointer items-center justify-center border border-(--site-header-line) bg-(--site-header-ctl-bg) text-(--site-header-ink)"
                        >
                            <UiIcon name="menu" size={22} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </header>

            {isMenuMounted ? (
                <NavigationOffCanvas open={isMenuOpen} onClose={() => setIsMenuOpen(false)} isScrolled={isScrolled} />
            ) : null}
        </>
    );
}

export default Header;
